import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { referenceColors } from '../../shared/tokens';
import { addColorOpacity } from '../../shared/utils/styles';

export type PropertyStatus = 'research' | 'under_contract' | 'owned' | 'listed_for_sale' | 'sold';

export interface StatusBadgeProps {
  status: PropertyStatus;
  size?: 'sm' | 'md' | 'lg';
  style?: any;
}

interface StatusConfig {
  label: string;
  color: string;
  bgOpacity: number;
}

export function StatusBadge({ status, size = 'md', style }: StatusBadgeProps) {
  const { tokens } = useTheme();
  
  // Use theme-based colors with proper semantic meaning and readability
  // Labels are human-friendly, avoiding jargon
  const statusConfig: Record<PropertyStatus, StatusConfig> = {
    research: {
      label: 'Looking At',
      color: '#F97316', // Orange - readable and signals exploration
      bgOpacity: 0.12,
    },
    under_contract: {
      label: 'Buying',
      color: '#3B82F6', // Blue - signals in-progress
      bgOpacity: 0.12,
    },
    owned: {
      label: 'Owned',
      color: tokens.color.success, // Green for success/owned
      bgOpacity: 0.12,
    },
    listed_for_sale: {
      label: 'Selling',
      color: '#A855F7', // Purple - signals active listing
      bgOpacity: 0.12,
    },
    sold: {
      label: 'Sold',
      color: addColorOpacity(tokens.color.onBackground, 0.6), // Grey for completed/archived
      bgOpacity: 0.08,
    },
  };

  const config = statusConfig[status] || statusConfig.research;
  
  const sizeStyles = {
    sm: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      fontSize: 10,
      fontWeight: '600' as const,
      borderRadius: 4,
    },
    md: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      fontSize: 11,
      fontWeight: '600' as const,
      borderRadius: 5,
    },
    lg: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      fontSize: 12,
      fontWeight: '600' as const,
      borderRadius: 6,
    },
  };

  const currentSize = sizeStyles[size];

  return (
    <View
      style={[
        {
          paddingHorizontal: currentSize.paddingHorizontal,
          paddingVertical: currentSize.paddingVertical,
          borderRadius: currentSize.borderRadius,
          backgroundColor: addColorOpacity(config.color, config.bgOpacity),
          borderWidth: 1,
          borderColor: addColorOpacity(config.color, 0.25),
          alignSelf: 'flex-start',
        },
        style,
      ]}
    >
      <Text
        style={{
          fontSize: currentSize.fontSize,
          fontWeight: currentSize.fontWeight,
          color: config.color,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {config.label}
      </Text>
    </View>
  );
}

